
import { createContext, useContext, onMount } from "solid-js";
import { createStore } from "solid-js/store";
import socket from "./socket";

const AdminContext = createContext();

type Pipeline = {
  id: number;
  user_id: number;
  status: string;
  sha?: string;
  ref?: string;
  detailed_status?: string;
  duration?: number;
  finished_at?: string;
  created?: string;
  pipeline_id?: number;
};

type Problem = {
  problem_key: string;
  default_starting_date: string | null;
  starting_date_override: string | null;
  effective_starting_date: string | null;
  last_updated: string;
  missing_from_latest_pipeline: boolean;
  starting_date_too_old: boolean;
  has_starting_date_override: boolean;
};

type AdminStore = {
  users: any[];
  results: Record<string, any>;
  resultsFetching: boolean;
  pipelines: Record<number, Pipeline>;
  problems: Problem[];
  problemsImporting: boolean;
  problemsImportMessage: string | null;
};

export function AdminContextProvider(props: any) {
  const [adminStore, setAdminStore] = createStore<AdminStore>({
    users: [],
    results: {},
    resultsFetching: false,
    pipelines: {} as Record<number, Pipeline>,
    problems: [],
    problemsImporting: false,
    problemsImportMessage: null
  });


  
  onMount(() => {
    console.log('Mounted AdminContext');
    socket.on("user-list", (users) => {
      setAdminStore({users});
    });

    socket.on('results', (results) => {
        setAdminStore({results, resultsFetching: false});
    });
    socket.on('pipeline-triggered', (payload: { ok: boolean; user_id: number; error?: string; pipeline?: any; }) => {
      if (!payload) return;
      // lightweight feedback via console for now; could be extended to toasts
      if (payload.ok) {
        console.log('Pipeline triggered for user', payload.user_id, payload.pipeline);
      } else {
        console.warn('Pipeline trigger failed for user', payload.user_id, payload.error);
      }
    });

    socket.on('pipelines-latest', (latest: Record<number, Pipeline>) => {
      setAdminStore({ pipelines: latest || {} as Record<number, Pipeline> });
    });
    socket.on('pipeline-updated', ({ user_id, pipeline }: { user_id: number, pipeline: Pipeline }) => {
      setAdminStore('pipelines', user_id, pipeline as Pipeline);
    });
    socket.on('problem-list', (problems: Problem[]) => {
      setAdminStore({ problems: problems || [] });
    });
    socket.on('problems-imported', (result: { ok: boolean; count?: number; pipelineId?: number; error?: string }) => {
      setAdminStore({
        problemsImporting: false,
        problemsImportMessage: result.ok
          ? `${result.count} problèmes importés depuis le pipeline ${result.pipelineId}.`
          : result.error || 'Import des problèmes impossible.'
      });
    });

  });

  const actions = {
    editUser: (index: number) => {
      const user = adminStore.users[index];
      const fields = {
        role: user.roleInput,
        project_id: user.project_idInput
      };
      socket.emit('update-user', {id: user.id, fields});
      setAdminStore('users', index, 'editing', false);
    },
    deleteUser: (user_id: number) => {
      socket.emit('delete-user', user_id);
      setAdminStore('users', (u: any[]) => u.filter((user) => user.id !== user_id));
    },
    addUser: (fields: any) => {
      socket.emit('add-user', fields);
    }
  }

  return (
    <AdminContext.Provider value={[adminStore, setAdminStore, actions]}>
      {props.children}
    </AdminContext.Provider>
  );
}

export function useAdminContext() { return useContext(AdminContext); }
